import React, { useState } from 'react';
import Logo from './Logo';
import LanguageSwitcher from './ui/LanguageSwitcher';
import AnimatedInsuranceLink from './ui/AnimatedInsuranceLink';
import { LanguageOption } from '../types';
import i18n from '../i18n/config';
import { Menu, X, ChevronDown, Home, Car, CarFront, Briefcase, Wallet, HeartHandshake, RefreshCcw, House } from 'lucide-react';

interface HeaderProps {
  currentLanguage: string;
}

const Header: React.FC<HeaderProps> = ({ currentLanguage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  
  const languageOptions: LanguageOption[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'lv', label: 'Latviešu', flag: '🇱🇻' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  ];

  const loanTypes = [
    {
      id: 'consumer',
      title: i18n.t('loan_types_page.consumer.title'),
      icon: Wallet,
      route: i18n.t('routes.loan_type_consumer')
    },
    {
      id: 'credit_consolidation',
      title: i18n.t('loan_types_page.credit_consolidation.title'),
      icon: HeartHandshake,
      route: i18n.t('routes.loan_type_credit_consolidation')
    },
    {
      id: 'mortgage',
      title: i18n.t('loan_types_page.mortgage.title'),
      icon: Home,
      route: i18n.t('routes.loan_type_mortgage')
    },
    {
      id: 'car',
      title: i18n.t('loan_types_page.car.title'),
      icon: Car,
      route: i18n.t('routes.loan_type_car')
    },
    {
      id: 'auto_leasing',
      title: i18n.t('loan_types_page.auto_leasing.title'),
      icon: CarFront,
      route: i18n.t('routes.loan_type_auto_leasing')
    },
    {
      id: 'recreditation',
      title: i18n.t('loan_types_page.recreditation.title'),
      icon: RefreshCcw,
      route: i18n.t('routes.loan_type_recreditation')
    },
    {
      id: 'business',
      title: i18n.t('loan_types_page.business.title'),
      icon: Briefcase,
      route: i18n.t('routes.loan_type_business')
    },
    {
      id: 'home_credit',
      title: i18n.t('loan_types_page.home_credit.title'),
      icon: House,
      route: i18n.t('routes.loan_type_home_credit')
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'unset';
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    document.body.style.overflow = 'unset';
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  const closeServices = () => {
    setIsServicesOpen(false);
  };

  const insuranceUrl = `https://apdrosini.kreditiem.lv/${currentLanguage}/`;

  const desktopNavLinks = (
    <>
      <div className="relative">
        <button
          onClick={toggleServices}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setTimeout(closeServices, 150);
            }
          }}
          className="flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200 focus:outline-none"
        >
          <span>{i18n.t('footer.quick_links.services')}</span>
          <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
        </button>

        {isServicesOpen && (
          <div className="absolute top-full left-0 lg:left-auto lg:right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            {loanTypes.map((loanType) => {
              const IconComponent = loanType.icon;
              return (
                <a
                  key={loanType.id}
                  href={`/${loanType.route}`}
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors duration-200"
                  onClick={closeServices}
                >
                  <IconComponent className="h-5 w-5 mr-3 text-gray-500" />
                  <span className="text-sm font-medium">{loanType.title}</span>
                </a>
              );
            })}
          </div>
        )}
      </div>

      <AnimatedInsuranceLink
        href={insuranceUrl}
        label={i18n.t('footer.quick_links.insurance')}
      />
      <a
        href={`#${i18n.t('anchor_links.about_us')}`}
        className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
      >
        {i18n.t('about.title')}
      </a>
      <a
        href={`/${i18n.t('routes.contact')}`}
        className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
      >
        {i18n.t('contact.title')}
      </a>
      <a
        href={`#${i18n.t('anchor_links.faq')}`}
        className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
      >
        {i18n.t('footer.quick_links.faq')}
      </a>
    </>
  );

  return (
    <header className="bg-white shadow-sm relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile header */}
        <div className="flex sm:hidden justify-between items-center py-4">
          <Logo />
          <button
            className="p-2 rounded-md text-gray-600 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Desktop / tablet header: 2 rows below lg, single row at lg+ */}
        <div className="hidden sm:flex flex-col lg:flex-row lg:justify-between lg:items-center py-4 gap-3 lg:gap-0">
          <div className="flex justify-between items-center w-full lg:w-auto">
            <Logo />
            <div className="lg:hidden">
              <LanguageSwitcher
                options={languageOptions}
                currentLanguage={currentLanguage}
              />
            </div>
          </div>

          <nav className="flex flex-wrap items-center justify-center lg:justify-end gap-x-6 gap-y-2 w-full lg:w-auto">
            {desktopNavLinks}
            <div className="hidden lg:block">
              <LanguageSwitcher
                options={languageOptions}
                currentLanguage={currentLanguage}
              />
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-50 transform transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        } sm:hidden`}
      >
        {/* Fullscreen Menu */}
        <div className="absolute inset-0 bg-white">
          <div className="flex flex-col h-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="flex justify-between items-center py-4">
                <Logo />
                <button
                  className="p-2 rounded-md text-gray-600 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto">
              {/* Services Dropdown */}
              <div className="space-y-2">
                <button
                  onClick={toggleServices}
                  className="flex text-left w-full px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                >
                  <ChevronDown className={`h-5 w-5 mr-2 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                  <span>{i18n.t('footer.quick_links.services')}</span>
                </button>
                
                {isServicesOpen && (
                  <div className="ml-4 space-y-1">
                    {loanTypes.map((loanType) => {
                      const IconComponent = loanType.icon;
                      return (
                        <a
                          key={loanType.id}
                          href={`/${loanType.route}`}
                          className="flex items-center px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                          onClick={closeMobileMenu}
                        >
                          <IconComponent 
                            className="h-5 w-5 mr-3 text-gray-500" 
                          />
                          <span className="font-medium">{loanType.title}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-6 space-y-2">
                <AnimatedInsuranceLink
                  href={insuranceUrl}
                  label={i18n.t('footer.quick_links.insurance')}
                  className="block w-fit mx-4 text-xl"
                  onClick={closeMobileMenu}
                />
                <a
                  href={`#${i18n.t('anchor_links.about_us')}`}
                  className="block px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  {i18n.t('about.title')}
                </a>
                <a
                  href={`/${i18n.t('routes.contact')}`}
                  className="block px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  {i18n.t('contact.title')}
                </a>
                <a
                  href={`#${i18n.t('anchor_links.faq')}`}
                  className="block px-4 py-3 text-xl font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  {i18n.t('footer.quick_links.faq')}
                </a>
              </div>
            </nav>

            <div className="p-6 border-t border-gray-200">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">{i18n.t('languages.select_language')}</h3>
                <LanguageSwitcher
                  options={languageOptions}
                  currentLanguage={currentLanguage}
                />
              </div>
              <p className="text-sm text-gray-500">
                {i18n.t('footer.copyright', { year: new Date().getFullYear() })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;